// src/components/financials/FundActivityReport.js
// Updated: 01/25/2026 - Implemented "All History" Calculation for Accurate Beginning Balances

import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Grid, TextField, Button, 
  Table, TableBody, TableCell, TableHead, TableRow, 
  CircularProgress, Divider
} from '@mui/material';
import { Print } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const FundActivityReport = () => {
  const { userProfile } = useAuth();
  const churchId = userProfile?.churchId;

  const currentYear = new Date().getFullYear();
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
  const [endDate, setEndDate] = useState(`${new Date().toISOString().split('T')[0]}`);
  
  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState({ beg: 0, income: 0, expense: 0, net: 0, end: 0 });
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  const generateReport = async () => {
    if (!churchId) return;
    setLoading(true);

    try {
      // 1. Fetch Fund Definitions (for Names)
      const fundsSnap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'funds'));
      const fundsMap = {};
      fundsSnap.docs.forEach(doc => {
        fundsMap[doc.id] = {
          id: doc.id,
          name: doc.data().name,
          begBalance: 0, // Calculated below
          income: 0,
          expense: 0,
          endBalance: 0  // Calculated below
        };
      });

      // 2. Fetch ALL Posted Transactions (No Date Filter in Query)
      // We fetch everything so we can calculate the "Beginning Balance" accurately
      const transQ = query(
        collection(db, 'churches', churchId, 'financials', 'data', 'transactions'),
        where('status', '==', 'posted')
      );
      const transSnap = await getDocs(transQ);

      transSnap.docs.forEach(doc => {
        const t = doc.data();
        const amt = parseFloat(t.amount) || 0;
        const txDate = t.date;

        // Determine which bucket this transaction falls into
        const isBeforeStart = txDate < startDate;
        const isDuringPeriod = txDate >= startDate && txDate <= endDate;

        const processSplit = (fundId, amount) => {
            if (!fundsMap[fundId]) return;

            if (isBeforeStart) {
                // Historical activity adds to Beginning Balance
                fundsMap[fundId].begBalance += amount;
            } else if (isDuringPeriod) {
                // Activity during period
                if (t.type === 'opening_balance') {
                     // Special Case: If an opening balance happens DURING the period,
                     // we treat it as an Adjustment/Balance increase, but visually 
                     // users often prefer seeing it as part of the "Start".
                     // For strict accounting: It is Income/Adj during this period.
                     fundsMap[fundId].income += amount;
                } else {
                     fundsMap[fundId].income += amount;
                }
            }
        };

        if (t.splits && t.splits.length > 0) {
            t.splits.forEach(s => processSplit(s.fundId, parseFloat(s.amount) || 0));
        } else {
            processSplit(t.fundId, amt);
        }
      });

      // 3. Fetch ALL Approved Expenses (No Date Filter)
      const expQ = query(
        collection(db, 'churches', churchId, 'financials', 'data', 'expenses'),
        where('status', '==', 'approved')
      );
      const expSnap = await getDocs(expQ);

      expSnap.docs.forEach(doc => {
        const e = doc.data();
        const amt = parseFloat(e.amount) || 0;
        const expDate = e.date;

        if (!fundsMap[e.fundId]) return;

        if (expDate < startDate) {
            // Expenses before start date reduce the Beginning Balance
            fundsMap[e.fundId].begBalance -= amt; // Assuming expense amount is positive in DB, we subtract it
        } else if (expDate >= startDate && expDate <= endDate) {
            fundsMap[e.fundId].expense += amt;
        }
      });

      // 4. Final Calculations & Totals
      let tBeg = 0, tInc = 0, tExp = 0, tNet = 0, tEnd = 0;

      const reportList = Object.values(fundsMap).map(f => {
          // End Balance = Beg + Inc - Exp
          f.endBalance = f.begBalance + f.income - f.expense;

          tBeg += f.begBalance;
          tInc += f.income;
          tExp += f.expense;
          tEnd += f.endBalance;
          return f;
      }).sort((a, b) => a.name.localeCompare(b.name));

      setReportData(reportList);
      setTotals({ beg: tBeg, income: tInc, expense: tExp, net: tInc - tExp, end: tEnd });
      setHasRun(true);

    } catch (e) {
      console.error(e);
      alert("Error: " + e.message);
    }
    setLoading(false);
  };

  return (
    <Box>
       {/* CONTROL BAR */}
       <Paper sx={{ p: 3, mb: 3, '@media print': { display: 'none' } }}>
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={3}>
                <TextField fullWidth type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Grid>
            <Grid item xs={6} md={3}>
                <TextField fullWidth type="date" label="End Date" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                <Button variant="contained" size="large" onClick={generateReport} disabled={loading}>
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Run Report"}
                </Button>
                <Button startIcon={<Print />} onClick={() => window.print()} sx={{ ml: 2 }}>Print / PDF</Button>
            </Grid>
        </Grid>
      </Paper>

      {/* REPORT BODY */}
      {hasRun && (
        <Paper sx={{ p: 4, mt: 2 }} id="printable-report">
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h5" fontWeight="bold">Fund Activity Report</Typography>
                <Typography variant="subtitle1">Period: {startDate} to {endDate}</Typography>
            </Box>

            <Table size="small">
                <TableHead>
                    <TableRow sx={{ bgcolor: '#fafafa', borderBottom: '2px solid #e0e0e0' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Fund Name</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Beg. Balance</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>+ Income</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>- Expense</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Net Change</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', borderLeft: '1px solid #eee' }}>End Balance</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {reportData.map((row) => {
                        const netChange = row.income - row.expense;
                        return (
                            <TableRow key={row.id} hover>
                                <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                                
                                {/* Accurate Beginning Balance (Calculated from History) */}
                                <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                    ${row.begBalance.toLocaleString(undefined, {minimumFractionDigits: 2})}
                                </TableCell>

                                <TableCell align="right">
                                    {row.income > 0 ? `$${row.income.toLocaleString(undefined, {minimumFractionDigits: 2})}` : '-'}
                                </TableCell>
                                <TableCell align="right">
                                    {row.expense > 0 ? `$${row.expense.toLocaleString(undefined, {minimumFractionDigits: 2})}` : '-'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', color: netChange < 0 ? 'error.main' : 'success.main' }}>
                                    {netChange !== 0 ? (netChange > 0 ? "+" : "") + `$${netChange.toLocaleString(undefined, {minimumFractionDigits: 2})}` : '-'}
                                </TableCell>
                                <TableCell align="right" sx={{ borderLeft: '1px solid #eee', fontWeight: 'bold' }}>
                                    ${row.endBalance.toLocaleString(undefined, {minimumFractionDigits: 2})}
                                </TableCell>
                            </TableRow>
                        );
                    })}

                    {/* TOTALS */}
                    <TableRow sx={{ bgcolor: '#f0f9ff', borderTop: '2px solid #1976d2' }}>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>TOTALS</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>${totals.beg.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>${totals.income.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>${totals.expense.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>${totals.net.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', borderLeft: '1px solid #ccc' }}>${totals.end.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            <Box sx={{ mt: 4 }}>
                <Typography variant="caption" color="textSecondary">
                    * "Beg. Balance" includes all activity prior to {startDate}. "End Balance" is the calculated total as of {endDate}.
                </Typography>
            </Box>
        </Paper>
      )}
    </Box>
  );
};

export default FundActivityReport;